<template>
  <div class="catalog-container">
    
    <!-- Teleport header actions to main header -->
     <Teleport v-if="isMounted" to="#additional-right-header-content">
      <div class="row items-center q-gutter-sm">
        <Search
          :placeholder="$t('Search catalogs...')"
          search-button-title="Search Catalogs"
          close-button-title="Close Search"
          @update:modelValue="handleSearchChanged"
          @open="handleSearchOpened"
          @close="handleSearchClosed"
        />
      </div>
    </Teleport>

   <!-- Categories List -->
    <div class="q-pa-md">
      <div class="q-gutter-md">
        <category-card
          v-for="(category, index) in filteredCategories" 
          :key="category.id"
          :category="category"
          :color-index="index"
          :expanded-category-id="expandedCategoriesSet.has(category.id) ? category.id : null"
          @toggle-category="toggleCategory"
          @open-surf="handleOpenSurf"
        />
      </div>
        <!-- No Results Message -->
      <div class="text-center q-pa-xl">
         <EmptyState 
          v-if="filteredCategories.length === 0 && searchQuery"
          icon="folder_open"
          :title="$t('No catalogs found')"
          :description='$t("No catalogs match your search for") + ` "${searchQuery}"`'
        />
      </div>

      <Map 
        ref="mapComponent"
        v-model:layerId="selectedLayerId"
        @expand="handleMapExpand"
        @close="handleMapClose"
        :show-preview="false"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance, onMounted } from 'vue'
import CategoryCard from '../catalogs/category-card.vue'
import Search from '@/common/components/ui/search.vue'
import Map from '@/common/components/maps/simple-map.vue' 
import EmptyState from '@/common/components/ui/empty-state.vue'


const props = defineProps({
  showDesktopLayout: {
    type: Boolean,
    default: false
  }
})

// State
const expandedCategories = ref([])
const searchQuery = ref('')
const categoryData = ref([])
const isMounted = ref(false)

const mapComponent = ref(null)
const selectedLayerId = ref(52) // Default layer

// Current component instance to access global methods
const { proxy } = getCurrentInstance()

// Lifecycle hooks
onMounted(async () => {
  // const params = {}
  // const shdcData = await proxy.get('DataCube/GetSimpleShdcData', { pars: JSON.stringify(this.params) })
  const shdcData = await proxy.get('DataCube/GetShdcData', {simple: true})
  categoryData.value = shdcData.map((shdcRow, index) => ({
    id: shdcRow.id,
    property: shdcRow.property,
    title: shdcRow.data_indicator__indicator_id_val,
    description: shdcRow.public_readable_description,
    fileName: shdcRow.file_name,
    layerId: shdcRow.id,
    metadata: `https://stac.ecodatacube.eu/${shdcRow.file_name}/collection.json`,
  }));
  isMounted.value = true;
})

// const response = await this.get("DataCube/GetShdcFiles", params)

// Computed property for filtered categories
const filteredCategories = computed(() => {
  // console.log(proxy.$store)
  if (!searchQuery.value) {
    return categoryData.value
  }
  const query = searchQuery.value.toLowerCase()
  return categoryData.value.filter(category => {
    const titleMatch = category.title.toLowerCase().includes(query)
    const descriptionMatch = category.description && category.description.toLowerCase().includes(query)
    return titleMatch || descriptionMatch
  })
})

// Computed property for expanded state - more efficient than inline computation
const expandedCategoriesSet = computed(() => {
  return new Set(expandedCategories.value)
})

const toggleCategory = (categoryId) => {
  // console.log('Toggling category:', categoryId, 'Current search:', searchQuery.value)
  const index = expandedCategories.value.indexOf(categoryId)
  if (index > -1) {
    // Category is expanded, remove it from the array
    expandedCategories.value.splice(index, 1)
  } else {
    // Category is not expanded, add it to the array
    expandedCategories.value.push(categoryId)
  }
  // Ensure search state is preserved
  // console.log('After toggle - Search query:', searchQuery.value)
}

const handleOpenSurf = async (surfData) => {  
  try {
    selectedLayerId.value = surfData.layerId
    
    if (mapComponent.value) {
      await mapComponent.value.handleExpand()
    }
    
  } catch (error) {
    console.error('Error opening surf:', error)
  }
}

// ✨ DODANO: Map event handlers (jednostavni)
const handleMapExpand = (data) => {
  // console.log('Map expanded:', data.layerId)
}

const handleMapClose = () => {
  // console.log('Map closed')
}

// Search event handlers
const handleSearchChanged = (query) => {
  // console.log('Search changed to:', query)
  searchQuery.value = query
}

const handleSearchOpened = () => {
  // console.log('Search opened')
}

const handleSearchClosed = () => {
  // console.log('Search closed - preserving current query:', searchQuery.value)
}

</script>

<style scoped>
.catalog-container {
  min-height: 100vh;
  width: 100%;
}
</style>